import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "api/axios";
import Approve from "./components/approve";
import Refuse from "./components/refuse";
import jwtDecode from "jwt-decode";

const ReportDetails = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [targetCars, setTargetCars] = useState("");

  useEffect(() => {
    if (!reportId) return;

    const fetchReport = async () => {
      setLoading(true);
      try {
        const rawToken = localStorage.getItem("token")?.replace(/"/g, "");
        const token = "Bearer " + rawToken;

        let decoded = null;
        let role = null;
        if (rawToken) {
          try {
            decoded = jwtDecode(rawToken);
            role =
              decoded.role ||
              decoded[
                "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
              ];
          } catch (err) {
            console.error("Failed to decode token", err);
          }
          //console.log("👮 Roli nga JWT:", role);
        }

        const response = await axios.get(`/api/reports/report/${reportId}`, {
          headers: { Authorization: token },
        });

        const reportData =
          response.data.result || response.data[0] || response.data;

        setReport({ ...reportData, role });
      } catch (error) {
        console.error("Failed to fetch report details", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId]);

  if (!reportId) return <div>Nuk u dërgua ID e raportit.</div>;
  if (loading) return <div>Duke ngarkuar detajet e raportit...</div>;
  if (!report) return <div>Raporti nuk u gjet.</div>;

  const formattedDate = new Date(report.date).toLocaleString("sq-AL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  const role = report?.role;

  // ✅ Normalize approval checks
  const isAdminPending =
    role === "admin" || role === "admin_assistant" && 
    (report.approved === false ||
      report.approved === null ||
      report.approved === undefined);

  const isPolicePending =
    role === "admin_police" || role === "assistant_police" &&
    (report.isApprovedByPolice === false ||
      report.isApprovedByPolice === null ||
      report.isApprovedByPolice === undefined);

  // ✅ Better status mapping
  const getStatus = () => {
    if (role === "admin" || role === "admin_assistant") {
      if (report.approved === true) return "Miratuar nga Admin";
      if (report.approved === false) return "Refuzuar nga Admin";
      return "Në pritje nga Admin";
    }
    if (role === "admin_police" || role === "assistant_police") {
      if (report.isApprovedByPolice === true) return "Miratuar nga Policia";
      if (report.isApprovedByPolice === false) return "Refuzuar nga Policia";
      return "Në pritje nga Policia";
    }
    return (
      `Admin: ${
        report.approved === true
          ? "Miratuar"
          : report.approved === false
          ? "Refuzuar"
          : "Në pritje"
      }, ` +
      `Policia: ${
        report.isApprovedByPolice === true
          ? "Miratuar"
          : report.isApprovedByPolice === false
          ? "Refuzuar"
          : "Në pritje"
      }`
    );
  };

  const combinedMedia = [...(report.photos || []), ...(report.videos || [])];

  // const isVideo = (media) =>
  //   media.endsWith(".mp4") || media.endsWith(".avi") || media.endsWith(".mov");

    const isVideo = (media) => {
  if (!media || typeof media !== "string") return false; // ✅ guard
  const lower = media.split("?")[0].toLowerCase();      // remove query params
  return lower.endsWith(".mp4") || lower.endsWith(".avi") || lower.endsWith(".mov");
};

  const prevSlide = () => {
    const newIndex =
      (currentIndex - 1 + combinedMedia.length) % combinedMedia.length;
    setCurrentIndex(newIndex);
  };

  const nextSlide = () => {
    const newIndex = (currentIndex + 1) % combinedMedia.length;
    setCurrentIndex(newIndex);
  };

  return (
    <div className="rounded-xl bg-white p-6 shadow-lg">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          Detajet e raportit #{report.reportName}
        </h1>
      </div>
      <hr />

      {/* Report Info */}
      <div className="mt-4 space-y-2">
                 <p>
              <span className="font-bold">Qyteti:</span>{" "}
              {report?.address
                ? (() => {
                    const gpsMatch =
                      report.address.match(/\(([^)]+)\)/);
                    const cityName = report.address
                      .split("(")[0]
                      .trim();

                    if (gpsMatch && gpsMatch[1]) {
                      const [lat, lng] = gpsMatch[1]
                        .split(",")
                        .map((x) => x.trim());
                      const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

                      return (
                        <>
                          <span>{cityName} </span>
                          <span>(</span>
                          <a
                            href={googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cursor-pointer font-semibold text-blue-600 underline"
                            title="Shiko lokacionin në Google Maps"
                          >
                            {lat}, {lng}
                          </a>
                          <span>)</span>
                        </>
                      );
                    }
                    return <span>{report?.address}</span>;
                  })()
                : "Unknown"}
            </p>
        <p>
          <strong>Kategoria:</strong> {report.category}
        </p>
        <p>
          <strong>Data:</strong> {formattedDate}
        </p>
        <p>
          <strong>Përshkrimi:</strong> {report.textContent || "N/A"}
        </p>
        <p>
          <strong>Statusi:</strong> {getStatus()}
        </p>
      </div>

      {/* Media Carousel */}
      {combinedMedia.length > 0 && (
        <div className="relative mx-auto mt-6 flex max-w-2xl items-center justify-center">
          {isVideo(combinedMedia[currentIndex]) ? (
            <video
              src={combinedMedia[currentIndex]}
              className="max-h-80 w-auto max-w-full rounded-lg object-contain shadow"
              controls
            />
          ) : (
            <img
              src={combinedMedia[currentIndex]}
              className="max-h-80 w-auto max-w-full cursor-pointer rounded-lg object-contain shadow transition hover:scale-105"
              alt={`media-${currentIndex + 1}`}
              onClick={() => setZoomedImage(combinedMedia[currentIndex])}
            />
          )}

          {combinedMedia.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="btn btn-circle absolute left-2 bg-white text-brand-700 shadow-md hover:text-white"
              >
                ❮
              </button>
              <button
                onClick={nextSlide}
                className="btn btn-circle absolute right-2 bg-white text-brand-700 shadow-md hover:text-white"
              >
                ❯
              </button>
            </>
          )}
        </div>
      )}

      {/* Target Cars */}
      {/* <div className="mt-6">
        {role === "admin" && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Targat e makinës
            </label>
            <input
              type="text"
              value={targetCars}
              onChange={(e) => setTargetCars(e.target.value)}
              placeholder="Shënoni targat (p.sh., ABC-123, XYZ-789)"
              className="w-full rounded-lg border border-gray-300 p-2 focus:border-brand-500 focus:ring focus:ring-brand-200"
            />
          </>
        )}

        {role === "admin_police" && targetCars && (
          <p>
            <strong>Targat e makinës:</strong> {targetCars}
          </p>
        )}
      </div> */}

      {/* Footer */}
      <footer className="mt-6 flex justify-end gap-2">
        <button
          onClick={() => navigate(-1)}
          className="btn border-0 bg-white text-brand-700 hover:text-white"
        >
          Kthehu
        </button>

        {(isAdminPending || isPolicePending) && (
          <>
            <button
              className="btn border-0 bg-red-500 text-white hover:bg-red-600"
              onClick={() => document.getElementById("my-modal-6")?.click()}
            >
              Refuzo
            </button>
            <Approve id={report?.id} targetCars={targetCars}>
              {(sendApproval) => (
                <button
                  className="btn border-0 bg-brand-500 text-white hover:bg-brand-700"
                  onClick={sendApproval}
                >
                  Aprovo
                </button>
              )}
            </Approve>
            <Refuse id={report?.id} />
          </>
        )}
      </footer>
    </div>
  );
};

export default ReportDetails;
